import { ROUTES } from "@/constants/routes";
import { route } from "@/utils/route";
import { getEntityData } from "@cohesive/ui-components";

/**
 * Generic entities (`base: "generic"`) carry their real platform in
 * `data.platform_name` and the remote record id in `data.id`. Selectors emit the
 * canonical platform strings — note the inconsistent hyphen vs underscore casing
 * (`google-drive`/`google-docs` but `google_sheets`/`google_calendar`) — while
 * the canvas-edit agent emits free-form values. This map collapses both onto one
 * internal key per platform.
 */
const PLATFORM_ALIASES: Record<string, string> = {
  gmail: "gmail",
  email: "gmail",

  "google-drive": "google-drive",
  google_drive: "google-drive",
  drive: "google-drive",
  "google drive": "google-drive",

  "google-docs": "google-docs",
  google_docs: "google-docs",
  docs: "google-docs",
  "google docs": "google-docs",

  "google-sheets": "google-sheets",
  google_sheets: "google-sheets",
  sheets: "google-sheets",
  "google sheets": "google-sheets",

  "google-calendar": "google-calendar",
  google_calendar: "google-calendar",
  calendar: "google-calendar",
  gcal: "google-calendar",
  "google calendar": "google-calendar",

  slack: "slack",
  notion: "notion",
  granola: "granola",

  github: "github",
};

export type GithubEntityKind = "repo" | "project";

export interface GithubEntityRef {
  kind: GithubEntityKind;
  /** Repository owner, when kind === "repo". */
  owner?: string;
  /** Repository name, when kind === "repo". */
  repo?: string;
  /** ProjectV2 GraphQL node id, when kind === "project". */
  projectId?: string;
}

/**
 * GitHub repos/projects reach the canvas as generic entities whose `data` only
 * carries `{id, url, title, ...}`. Classify repo vs project and pull the
 * identifiers a detail pane needs to fetch full data:
 * - Projects: ProjectV2 node ids are prefixed `PVT_`; the URL is `/projects/<n>`.
 * - Repos: parsed from a typed entity's `full_name`, else from the `github.com`
 *   URL (`github.com/<owner>/<repo>`).
 * Returns null when neither can be determined (caller falls back to generic).
 */
export function resolveGithubEntityRef(data: {
  id?: string | number;
  url?: string;
  full_name?: string;
}): GithubEntityRef | null {
  const idStr = data?.id != null ? String(data.id) : "";
  const url = typeof data?.url === "string" ? data.url : "";

  // Project: only a PVT_ node id is fetchable via the project-get action.
  if (idStr.startsWith("PVT_")) {
    return { kind: "project", projectId: idStr };
  }
  if (/\/projects\/\d+/.test(url)) {
    // A /projects/ URL without a usable node id can't be fetched — fall back.
    return null;
  }

  // Repo (typed entity): full_name is "owner/repo".
  if (typeof data?.full_name === "string" && data.full_name.includes("/")) {
    const [owner, repo] = data.full_name.split("/");
    if (owner && repo) return { kind: "repo", owner, repo };
  }

  // Repo (generic entity): parse owner/repo from the github.com URL.
  const match = url.match(/github\.com\/([^/]+)\/([^/?#]+)/);
  if (match) {
    const [, owner, repo] = match;
    if (owner !== "orgs" && owner !== "users" && repo !== "projects") {
      return { kind: "repo", owner, repo };
    }
  }

  return null;
}

/** Collapse a raw platform_name to its internal key, or null if unrecognized. */
export function normalizePlatformName(raw?: string | null): string | null {
  if (!raw) return null;
  return PLATFORM_ALIASES[raw.trim().toLowerCase()] ?? null;
}

export interface GenericPaneTarget {
  to: string;
  state: { entity: unknown };
}

/**
 * Maps a generic entity to the appNavigate target for its platform detail pane,
 * or null when the platform has no dedicated pane (the caller then falls through
 * to the generic EntityDetailPane). Docs/Sheets ride the Drive pane — it reads
 * `platform_name` off the entity in route state to pick the right preview URL.
 */
export function resolveGenericEntityPaneTarget(
  entity: unknown,
): GenericPaneTarget | null {
  const data = getEntityData(entity as never) as
    | {
        id?: string | number;
        platform_name?: string;
        url?: string;
        full_name?: string;
      }
    | undefined;

  const id = data?.id;
  if (id == null || id === "") return null;

  const platform = normalizePlatformName(data?.platform_name);
  if (!platform) return null;

  const idStr = id.toString();
  const state = { entity };

  switch (platform) {
    case "gmail":
      return {
        to: route(ROUTES.CANVAS_EXPERIMENTS.EMAIL_DETAIL, { emailId: idStr }),
        state,
      };
    case "notion":
      return {
        to: route(ROUTES.CANVAS_EXPERIMENTS.NOTION_DETAIL, { pageId: idStr }),
        state,
      };
    case "google-drive":
    case "google-docs":
    case "google-sheets":
      return {
        to: route(ROUTES.GOOGLE_DRIVE.FILE_DETAIL, { fileId: idStr }),
        state,
      };
    case "google-calendar":
      return {
        to: route(ROUTES.GOOGLE_CALENDAR.EVENT_DETAIL, { eventId: idStr }),
        state,
      };
    case "slack":
      return {
        to: route(ROUTES.SLACK.MESSAGE_DETAIL, { messageId: idStr }),
        state,
      };
    case "granola":
      return {
        to: route(ROUTES.GRANOLA.MEETING_DETAIL, { meetingId: idStr }),
        state,
      };
    case "github": {
      const ref = resolveGithubEntityRef(data ?? {});
      if (ref?.kind === "project") {
        return {
          to: route(ROUTES.GITHUB.PROJECT_DETAIL, { projectId: idStr }),
          state,
        };
      }
      if (ref?.kind === "repo") {
        return {
          to: route(ROUTES.GITHUB.REPO_DETAIL, { repoId: idStr }),
          state,
        };
      }
      return null;
    }
    default:
      return null;
  }
}
